import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  useCohortProfileQuery,
  useRenameCohortMutation,
  useRenameCohortProfileMutation,
} from '@verantos-dev/data-access-cohorts';

import { useCohortModalContext } from '../context/modalContext';

export const useRenameCohortModal = () => {
  const { itemId, closeDialog } = useCohortModalContext();
  const { projectId } = useParams<{ projectId: string }>();
  const [name, setName] = useState('');

  // Gen-3 uses the keyed TanStack mutation so the project-page artifact manifest
  // + activity feed refresh on rename (OR-6111); legacy keeps the RTK mutation
  // untouched. Both hooks called unconditionally (rules of hooks).
  // TODO: cleanup with research_assistant_gen_3 ff — drop the RTK branch.
  const isGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false) as boolean;
  const [rtkRenameMutation, { isLoading: rtkIsLoading, error: rtkError }] =
    useRenameCohortMutation();
  const gen3RenameMutation = useRenameCohortProfileMutation();
  const isLoading = isGen3Enabled ? gen3RenameMutation.isPending : rtkIsLoading;
  const error = isGen3Enabled ? gen3RenameMutation.error ?? undefined : rtkError;
  const { data: cohort, isLoading: loadingCohort } = useCohortProfileQuery({
    cohortId: itemId || '',
    projectId: projectId || '',
  });

  useEffect(() => {
    if (cohort?.cohortName) {
      setName(cohort.cohortName);
    }
  }, [cohort?.cohortName]);

  const isRenamed = cohort?.cohortName !== name.trim();

  const onChangeName = (_: string, value: string) => {
    setName(value);
  };

  const onSubmit = async () => {
    if (projectId && itemId) {
      const renameArgs = { projectId, cohortId: itemId, data: { cohortName: name.trim() } };
      if (isGen3Enabled) {
        await gen3RenameMutation.mutateAsync(renameArgs);
      } else {
        await rtkRenameMutation(renameArgs);
      }
    }
  };

  const isValidForSubmit = !loadingCohort && !isLoading && Boolean(name.trim()) && isRenamed;

  return {
    name,
    onChangeName,
    isSubmitting: isLoading,
    renameCohortError: error,
    isInitialLoading: loadingCohort,
    isValidForSubmit,
    cohort,
    onClose: closeDialog,
    onSubmit,
  };
};
